<template>
  <hs-modal
    :model-value="modelValue"
    title="Inventory policy"
    description="Control how open tickets reserve stock for the selected store."
    size="sm"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div v-if="settings" class="inventory-policy">
      <div class="inventory-policy__status">
        <span :class="['inventory-policy__dot', { 'is-active': effectiveEnabled }]" />
        <div>
          <strong>{{ effectiveEnabled ? 'Commitments active' : 'Physical inventory only' }}</strong>
          <p>{{ policySummary }}</p>
        </div>
      </div>

      <hs-checkbox
        v-model="trackingEnabled"
        label="Reserve inventory for open tickets"
        description="Track held and available stock before a ticket is paid."
        :disabled="saving || !canChangeSettings"
      />

      <hs-select
        v-model="shortagePolicy"
        :options="shortageOptions"
        label="When inventory is insufficient"
        icon="shield"
        :disabled="saving || !canChangeSettings || shortagesBlockedByRraEbm"
        :hint="shortageHint"
      />

      <div v-if="!settings.global_feature_enabled" class="inventory-policy__notice">
        Global rollout is disabled. You can prepare this store now; the policy becomes effective after global approval.
      </div>
      <div v-if="!canChangeSettings" class="inventory-policy__notice">
        You have read-only access to this policy.
      </div>
      <div v-if="error" class="inventory-policy__error">{{ error }}</div>
    </div>

    <div v-else class="inventory-policy__empty">
      Inventory policy is unavailable for this store.
    </div>

    <template #footer>
      <hs-button variant="secondary" :disabled="saving" @click="close">Close</hs-button>
      <hs-button
        v-if="canChangeSettings"
        icon="save"
        :loading="saving"
        :disabled="saving || !hasChanges"
        @click="save"
      >
        Save policy
      </hs-button>
    </template>
  </hs-modal>
</template>

<script>
import { HsButton, HsCheckbox, HsModal, HsSelect } from "@/components/ui";
import http from "@/helper/http";
import api from "@/helper/api";

export default {
  name: "InventoryPolicyModal",
  components: { HsButton, HsCheckbox, HsModal, HsSelect },
  props: {
    modelValue: { type: Boolean, default: false },
    storeId: { type: [Number, String], default: null },
    settings: { type: Object, default: null },
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      trackingEnabled: false,
      shortagePolicy: "BLOCK",
      saving: false,
      error: "",
      shortageOptions: [
        { value: "BLOCK", label: "Block ticket" },
        { value: "ALLOW_WITH_PERMISSION", label: "Allow authorized users" },
      ],
    };
  },
  computed: {
    canChangeSettings() {
      return this.settings?.capabilities?.can_change_settings === true;
    },
    shortagesBlockedByRraEbm() {
      return this.settings?.shortages_blocked_by_rra_ebm === true;
    },
    effectiveEnabled() {
      return this.settings?.effective_commitment_tracking_enabled === true;
    },
    hasChanges() {
      return Boolean(this.settings) && (
        this.trackingEnabled !== this.settings.commitment_tracking_enabled ||
        this.shortagePolicy !== this.settings.shortage_policy
      );
    },
    policySummary() {
      if (!this.settings?.global_feature_enabled) {
        return "Commitments are paused globally.";
      }
      if (!this.settings?.commitment_tracking_enabled) {
        return "Open tickets do not reserve stock.";
      }
      return "Open tickets reserve stock before payment.";
    },
    shortageHint() {
      if (this.shortagesBlockedByRraEbm) {
        return "RRA EBM requires shortages to be blocked.";
      }
      return this.shortagePolicy === "ALLOW_WITH_PERMISSION"
        ? "Only users with shortage permission can continue."
        : "Tickets cannot continue without enough stock.";
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        if (open) this.resetDraft();
      },
    },
    settings() {
      if (this.modelValue) this.resetDraft();
    },
  },
  methods: {
    resetDraft() {
      this.trackingEnabled = this.settings?.commitment_tracking_enabled === true;
      this.shortagePolicy = this.settings?.shortage_policy || "BLOCK";
      this.error = "";
    },
    close() {
      if (!this.saving) this.$emit("update:modelValue", false);
    },
    async save() {
      if (!this.storeId || !this.canChangeSettings || !this.hasChanges) return;
      this.saving = true;
      this.error = "";
      try {
        const response = await http.patch(api.inventory_settings(this.storeId), {
          commitment_tracking_enabled: this.trackingEnabled,
          shortage_policy: this.shortagesBlockedByRraEbm ? "BLOCK" : this.shortagePolicy,
        });
        this.$emit("saved", response.data);
        this.$emit("update:modelValue", false);
      } catch (error) {
        this.error = error?.response?.data?.error?.message || "Unable to save inventory policy.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.inventory-policy { display: grid; gap: 1.15rem; }
.inventory-policy__status { align-items: flex-start; display: flex; gap: 0.75rem; }
.inventory-policy__status strong { color: var(--hs-text); font-size: 0.95rem; }
.inventory-policy__status p { color: var(--hs-muted); font-size: 0.8rem; margin: 0.2rem 0 0; }
.inventory-policy__dot { background: #94a3b8; border-radius: 999px; flex: 0 0 auto; height: 0.7rem; margin-top: 0.25rem; width: 0.7rem; }
.inventory-policy__dot.is-active { background: #16a34a; box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.12); }
.inventory-policy__notice { background: rgba(245, 158, 11, 0.09); border: 1px solid rgba(245, 158, 11, 0.28); border-radius: 8px; color: #92400e; font-size: 0.78rem; font-weight: 700; line-height: 1.5; padding: 0.75rem; }
.inventory-policy__error { color: var(--hs-danger, #b91c1c); font-size: 0.8rem; font-weight: 800; }
.inventory-policy__empty { color: var(--hs-muted); font-size: 0.86rem; }
</style>